import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useRegion } from '@/contexts/RegionContext';

const euCountries = [
  'at', 'be', 'bg', 'hr', 'cy', 'cz', 'dk', 'ee', 'fi', 'fr', 'de', 'gr', 
  'hu', 'ie', 'it', 'lv', 'lt', 'lu', 'mt', 'nl', 'pl', 'pt', 'ro', 'sk', 
  'si', 'es', 'se', 'gb'
];

export function PrivacyPolicyPage() {
  const { regionCode, loading: loadingRegion } = useRegion();

  let region = 'global';
  if (regionCode === 'za') {
    region = 'za';
  } else if (regionCode === 'us') {
    region = 'us';
  } else if (euCountries.includes(regionCode)) {
    region = 'eu';
  }

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground/30">
      <Helmet>
        <title>Privacy Policy | FIVE LENS Enneagram</title>
        <meta name="description" content="Our regional and global privacy policies and commitment to your data security." />
      </Helmet>
      
      <div className="max-w-4xl mx-auto px-6 lg:px-8 mb-8">
        <Breadcrumb items={[{ label: 'Privacy Policy' }]} />
      </div>

      <article className="max-w-4xl mx-auto px-6 lg:px-8 mb-20">
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-serif text-ink mb-6">Privacy & Data Policy</h1>
          <p className="text-ink/70 text-lg max-w-2xl mx-auto font-light leading-relaxed">
            At FIVE LENS, we take your privacy and the security of your psychometric data seriously. The following compliance framework and data handling protocols apply to your region based on your current location.
          </p>
        </header>

        <div className="bg-white border border-ground-deep shadow-sm overflow-hidden mb-12">
          {/* Content Area */}
          <div className="p-8 md:p-12 prose prose-brand max-w-none text-ink/80 font-light leading-relaxed">
            {loadingRegion && (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <p className="animate-pulse text-ink/70 font-bold uppercase tracking-widest text-sm">Detecting your region...</p>
              </div>
            )}

            {!loadingRegion && region === 'global' && (
              <div className="animate-in fade-in duration-500">
                <h2 className="text-2xl font-serif text-ink mb-4">Global Privacy Policy</h2>
                <p>Last Updated: October 2023</p>
                <p>
                  Ennea International and the FIVE LENS Development Platform are committed to protecting the privacy and security of your personal and psychometric data globally. We collect only the data necessary to provide you with your Personal Feedback Report (PFR) and related coaching or developmental services.
                </p>
                <h3 className="text-xl font-serif text-ink mt-6 mb-3">Data Collection</h3>
                <p>
                  We collect names, email addresses, and responses to the FIVE LENS Questionnaire. We do not sell your personal data to third parties.
                </p>
                <h3 className="text-xl font-serif text-ink mt-6 mb-3">Data Security</h3>
                <p>
                  All data is encrypted in transit and at rest using industry-standard protocols. Assessments are stored in secure, access-controlled databases. Access is strictly limited to certified practitioners actively engaged in your developmental journey.
                </p>
              </div>
            )}

            {!loadingRegion && region === 'za' && (
              <div className="animate-in fade-in duration-500">
                <h2 className="text-2xl font-serif text-ink mb-4">South Africa Privacy Notice (POPIA)</h2>
                <p className="mb-4">
                  As an organization headquartered in South Africa, Ennea International fully complies with the Protection of Personal Information Act (POPIA).
                </p>
                <h3 className="text-xl font-serif text-ink mt-6 mb-3">Processing of Personal Information</h3>
                <p>
                  Under POPIA, we act as the Responsible Party. We process your psychometric and personal information lawfully and in a reasonable manner that does not infringe your privacy. 
                </p>
                <h3 className="text-xl font-serif text-ink mt-6 mb-3">Your Rights</h3>
                <ul className="list-disc pl-5 space-y-2 mt-4">
                  <li><strong>Right of Access:</strong> You may request confirmation of whether we hold personal information about you.</li>
                  <li><strong>Right to Rectification:</strong> You may request the correction or deletion of personal information that is inaccurate, irrelevant, excessive, out of date, incomplete, or misleading.</li>
                  <li><strong>Right to Object:</strong> You may object, on reasonable grounds, to the processing of your personal information.</li>
                </ul>
                <p className="mt-4">
                  To exercise your rights under POPIA, please contact our Information Officer at <a href="mailto:info@enneainternational.com" className="text-rule-3 hover:underline">info@enneainternational.com</a>.
                </p>
              </div>
            )}

            {!loadingRegion && region === 'eu' && (
              <div className="animate-in fade-in duration-500">
                <h2 className="text-2xl font-serif text-ink mb-4">European Union Privacy Notice (GDPR)</h2>
                <p className="mb-4">
                  For residents of the European Economic Area (EEA), we adhere to the General Data Protection Regulation (GDPR), ensuring the highest standards of data privacy and transparency.
                </p>
                <h3 className="text-xl font-serif text-ink mt-6 mb-3">Legal Basis for Processing</h3>
                <p>
                  We process your data based on your explicit consent provided prior to taking the FIVE LENS Questionnaire, and for the legitimate interest of fulfilling the contract to provide your developmental reports.
                </p>
                <h3 className="text-xl font-serif text-ink mt-6 mb-3">Data Subject Rights</h3>
                <ul className="list-disc pl-5 space-y-2 mt-4">
                  <li><strong>Right to be Forgotten (Erasure):</strong> You have the right to request the permanent deletion of your assessment data and personal profile.</li>
                  <li><strong>Data Portability:</strong> You may request a copy of your psychometric data in a structured, commonly used, and machine-readable format.</li>
                  <li><strong>Right to Restriction:</strong> You may request we suspend processing of your data while a dispute is being resolved.</li>
                </ul>
                <p className="mt-4">
                  If you reside in the EU and wish to exercise these rights, please email us directly or utilize the data management portal provided by your certified practitioner.
                </p>
              </div>
            )}

            {!loadingRegion && region === 'us' && (
              <div className="animate-in fade-in duration-500">
                <h2 className="text-2xl font-serif text-ink mb-4">USA Privacy Notice (CCPA / CPRA)</h2>
                <p className="mb-4">
                  For residents of California and other applicable US states, this notice outlines your rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA).
                </p>
                <h3 className="text-xl font-serif text-ink mt-6 mb-3">Information We Collect</h3>
                <p>
                  We have collected the following categories of personal information within the last twelve (12) months: Identifiers (name, email address), commercial information (services purchased), and psychometric/inferential data drawn from the FIVE LENS Questionnaire to create a profile reflecting your preferences, characteristics, and psychological trends.
                </p>
                <h3 className="text-xl font-serif text-ink mt-6 mb-3">Do Not Sell My Personal Information</h3>
                <p>
                  <strong>FIVE LENS does not sell your personal information.</strong> We do not sell psychometric profiles or contact lists to third parties for advertising or direct marketing purposes. 
                </p>
                <h3 className="text-xl font-serif text-ink mt-6 mb-3">Your Rights</h3>
                <ul className="list-disc pl-5 space-y-2 mt-4">
                  <li><strong>Right to Know:</strong> You may request that we disclose certain information to you about our collection and use of your personal information over the past 12 months.</li>
                  <li><strong>Right to Delete:</strong> You have the right to request that we delete any of your personal information that we collected from you and retained, subject to certain exceptions.</li>
                  <li><strong>Non-Discrimination:</strong> We will not discriminate against you for exercising any of your CCPA rights.</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </article>
    </main>
  );
}
